import { t } from '@transifex/native';
import type { JSX, ChangeEvent } from 'react';
import { useEffect } from 'react';

import * as styles from 'pkg/config/styles';

import type { CreateChildPayload } from 'pkg/api/endpoints/accounts';
import { MIN_BIRTH_YEAR } from 'pkg/birth_date';
import DateTime from 'pkg/datetime';

import SectionDescription from 'components/SectionDescription';
import SectionTitle from 'components/section_title';
import { useStepModalContext } from 'components/step-modal';

import * as Input from 'components/form/inputs';
import { useJoinModalContext } from 'components/group/join-modal';
import Column from 'components/layout/column';

export default function Details(): JSX.Element {
	const joinContext = useJoinModalContext();
	const modalContext = useStepModalContext();

	const get = (key: keyof CreateChildPayload): string => {
		let value = joinContext.createPayload[key] || '';

		if (typeof value !== 'string') {
			value = value.toString();
		}

		return value;
	};

	const set = (event: ChangeEvent<HTMLInputElement>) => {
		const name = event.currentTarget.name;
		let value: string | number = event.currentTarget.value;

		if (event.currentTarget.type === 'numeric') {
			value = Number.parseInt(value, 10);
		}

		joinContext.setCreatePayload({ [name]: value });
	};

	const validYear = Number.parseInt(get('year'), 10) >= MIN_BIRTH_YEAR;
	const dob = [get('year'), get('month'), get('day')].join('-');
	const validDoB = DateTime.isValidISODateString(dob) && validYear;

	const canGoNext =
		validDoB && get('firstName').length > 0 && get('lastName').length > 0;

	useEffect(() => {
		modalContext.setCanGoNext(canGoNext);
	}, [canGoNext]);

	return (
		<Column spacing={styles.spacing._7}>
			<Column spacing={styles.spacing._1}>
				<SectionTitle>{t(`Please provide your child's details`)}</SectionTitle>
				<SectionDescription>{t('All fields are required.')}</SectionDescription>
			</Column>

			<Column spacing={styles.spacing._7}>
				<Input.Group label={t(`Child's first name`)}>
					<Input.Field
						testid="registration.child.firstname"
						required
						name="firstName"
						onChange={set}
						value={get('firstName')}
					/>
				</Input.Group>
				<Input.Group label={t(`Child's last name`)}>
					<Input.Field
						testid="registration.child.lastname"
						required
						name="lastName"
						onChange={set}
						value={get('lastName')}
					/>
				</Input.Group>

				<Input.BirthDate
					label={t(`Child's date of birth`)}
					year={get('year')}
					month={get('month')}
					day={get('day')}
					onChange={set}
					testid="registration.child"
				/>
			</Column>
		</Column>
	);
}
